import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Snackbar, Alert, AlertColor, SnackbarOrigin } from "@mui/material";
import { setNotificationHandler } from "utils/notificationService";

interface NotificationOptions {
  duration?: number;
  anchor?: SnackbarOrigin;
}

interface NotificationContextValue {
  showNotification: (message: string, severity: AlertColor, options?: NotificationOptions) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotification = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("info");
  const [duration, setDuration] = useState(3000);
  const [anchor, setAnchor] = useState<SnackbarOrigin>({ vertical: "top", horizontal: "center" });

  const showNotification = useCallback((msg: string, sev: AlertColor, options?: NotificationOptions) => {
    setMessage(msg);
    setSeverity(sev);
    setDuration(options?.duration ?? 3000);
    setAnchor(options?.anchor ?? { vertical: "top", horizontal: "center" });
    setOpen(true);
  }, []);

  useEffect(() => {
    setNotificationHandler(showNotification);
  }, [showNotification]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={anchor}
      >
        <Alert onClose={handleClose} severity={severity} variant="filled">
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
